import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FaFire, FaExternalLinkAlt, FaClock, FaUser, FaImage } from 'react-icons/fa';
import '../styles/RecentTrades.css';

const RecentTrades = ({ limit = 6, showTitle = true }) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentTrades = async () => {
      try {
        setLoading(true);

        // Fetch recent buy transactions
        const response = await axios.get(
          `https://api.mainnet.klever.org/v1.0/transaction/list?limit=50&type=17&status=success`,
          { timeout: 10000 }
        );

        const transactions = response.data.data.transactions || [];

        // Filter and process trades
        const processedTrades = transactions
          .filter(tx => {
            const hasMarketBuy = tx.contract?.some(c =>
              c.typeString === 'BuyContractType' && c.parameter?.buyType === 'MarketBuy'
            );
            const hasNFTReceipt = tx.receipts?.some(r =>
              r.assetId && r.assetId.includes('/')
            );
            return hasMarketBuy && hasNFTReceipt;
          })
          .slice(0, limit)
          .map(tx => {
            const buyContract = tx.contract.find(c => c.typeString === 'BuyContractType');
            const nftReceipt = tx.receipts.find(r => r.assetId && r.assetId.includes('/'));

            const [collectionId, nftId] = nftReceipt?.assetId?.split('/') || ['', ''];

            return {
              hash: tx.hash,
              timestamp: tx.timestamp,
              buyer: tx.sender,
              price: (buyContract?.parameter?.amount || 0) / 1000000,
              currencyId: buyContract?.parameter?.currencyId || 'KLV',
              collectionId,
              nftId,
              assetId: nftReceipt?.assetId,
              marketplaceId: nftReceipt?.marketplaceId
            };
          });

        // Cache for collection data
        const collectionCache = {};

        // Function to fetch NFT image
        const fetchNftImage = async (collectionId, nftId) => {
          try {
            let collectionData;
            if (collectionCache[collectionId]) {
              collectionData = collectionCache[collectionId];
            } else {
              const collectionRes = await axios.get(`https://api.mainnet.klever.org/v1.0/assets/${collectionId}`);
              collectionData = collectionRes.data.data.asset;
              collectionCache[collectionId] = collectionData;
            }

            const metadataObj = collectionData.uris?.find(uri => uri.key.toLowerCase() === 'metadata');
            let metadataUri = metadataObj?.value;

            if (!metadataUri) return null;

            if (metadataUri === "QmNaa2KQ6NkjjESpPHEnAow9hivnsAkq2Gd6R26cHG28Er") {
              metadataUri = `https://ipfs.io/ipfs/${metadataUri}/${nftId}.json`;
            } else {
              if (metadataUri.includes("klever-mint.mypinata.cloud")) {
                metadataUri = metadataUri.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
              }
              metadataUri = `${metadataUri}/${nftId}`;
            }

            try {
              const metadataRes = await axios.get(metadataUri);
              let image = metadataRes.data.image;

              if (image) {
                if (image.includes("www.lovemonsternft.com")) {
                  const imageName = image.split('/').pop();
                  image = `https://ipfs.io/ipfs/QmWVmUDPBeQzv6fG93JxQxFVee8b6smFD3RQosQXJHiZTJ/${imageName}`;
                } else if (image.includes("klever-mint.mypinata.cloud")) {
                  image = image.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
                }
              }

              return image;
            } catch (primaryError) {
              console.error('Primary metadata fetch failed, trying fallback:', primaryError);
              const fallbackUri = `https://${metadataUri.split('://')[1]}.ipfs.w3s.link/${nftId}`;
              const fallbackRes = await axios.get(fallbackUri);
              let image = fallbackRes.data.image;

              if (image) {
                if (image.includes("www.lovemonsternft.com")) {
                  const imageName = image.split('/').pop();
                  image = `https://ipfs.io/ipfs/QmWVmUDPBeQzv6fG93JxQxFVee8b6smFD3RQosQXJHiZTJ/${imageName}`;
                } else if (image.includes("klever-mint.mypinata.cloud")) {
                  image = image.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
                }
              }

              return image;
            }
          } catch (error) {
            console.error(`Error fetching image for ${collectionId}/${nftId}:`, error);
            return null;
          }
        };

        // Fetch images for all trades
        const tradesWithImages = await Promise.all(
          processedTrades.map(async (trade) => {
            const image = await fetchNftImage(trade.collectionId, trade.nftId);
            return { ...trade, image };
          })
        );

        setTrades(tradesWithImages);
      } catch (error) {
        console.error('Error fetching recent trades:', error);
        setTrades([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentTrades();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchRecentTrades, 30000);
    return () => clearInterval(interval);
  }, [limit]);

  const formatTimestamp = (timestamp) => {
    const now = Date.now();
    const txTime = timestamp * 1000; // Convert Unix timestamp to milliseconds
    const diff = now - txTime;
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return `${seconds}s ago`;
  };

  const truncateAddress = (address) => {
    if (!address) return 'Unknown';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  if (loading) {
    return (
      <div className="recent-trades-container">
        {showTitle && (
          <div className="recent-trades-header">
            <h2 className="recent-trades-title">
              <FaFire className="title-icon fire" />
              Recent Trades
            </h2>
          </div>
        )}
        <div className="recent-trades-loading">
          <div className="loading-spinner"></div>
          <p>Loading recent trades...</p>
        </div>
      </div>
    );
  }

  if (trades.length === 0) {
    return null; // Don't show section if no trades
  }

  return (
    <div className="recent-trades-container">
      {showTitle && (
        <div className="recent-trades-header">
          <h2 className="recent-trades-title">
            <FaFire className="title-icon fire" />
            Recent Trades
          </h2>
          <Link to="/collections" className="view-all-trades">
            View All
            <FaExternalLinkAlt className="external-icon" />
          </Link>
        </div>
      )}

      <div className="recent-trades-list">
        {trades.map((trade, index) => (
          <Link
            key={`${trade.hash}-${index}`}
            to={trade.collectionId && trade.nftId ? `/collections/${trade.collectionId}/${trade.nftId}` : '#'}
            className="trade-item"
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            {/* NFT Image */}
            <div className="trade-image-wrapper">
              {trade.image ? (
                <img src={trade.image} alt={`${trade.collectionId} #${trade.nftId}`} className="trade-image" />
              ) : (
                <div className="trade-image-placeholder">
                  <FaImage />
                </div>
              )}
            </div>

            {/* Trade Info */}
            <div className="trade-info">
              <div className="trade-primary">
                <h4 className="trade-nft-name">
                  {trade.collectionId} #{trade.nftId}
                </h4>
                <span className="trade-collection">{trade.collectionId}</span>
              </div>

              <div className="trade-secondary">
                <div className="trade-buyer">
                  <FaUser className="trade-icon" />
                  <span>{truncateAddress(trade.buyer)}</span>
                </div>
                <div className="trade-time">
                  <FaClock className="trade-icon" />
                  <span>{formatTimestamp(trade.timestamp)}</span>
                </div>
              </div>
            </div>

            {/* Price */}
            <div className="history-trade-price">
              <span className="history-price-amount">{trade.price.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}</span>
              <span className="history-price-currency">{trade.currencyId}</span>
            </div>

            {/* External Link */}
            <a
              href={`https://kleverscan.org/transaction/${trade.hash}`}
              target="_blank"
              rel="noopener noreferrer"
              className="trade-external-link"
              onClick={(e) => e.stopPropagation()}
            >
              <FaExternalLinkAlt />
            </a>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default RecentTrades;